/**
 * AI animation API (HTTP) plus small UI helpers (download, copy prompt, form submit).
 */
import ENV from "./Environment";
import Requests from "./Requests";
import {
  copyAnimationPromptToClipboard,
  downloadAnimationToDisk,
} from "../Main/AnimationGenerator/actions/animationFileActions";

const baseUrlGenerate = `${ENV.base_url}/api/v1/ai_animation/generate`;
const baseUrlStatus = `${ENV.base_url}/api/v1/ai_animation/status/`;
const baseUrlFetch = `${ENV.base_url}/api/v1/ai_animation/fetch/`;
const baseUrlView = `${ENV.base_url}/api/v1/ai_animation/view/`;
const baseUrlStream = `${ENV.base_url}/api/v1/ai_animation/stream/`;
const baseUrlDownload = `${ENV.base_url}/api/v1/ai_animation/download/`;
const baseUrlList = `${ENV.base_url}/api/v1/ai_animation/list`;
const baseUrlDelete = `${ENV.base_url}/api/v1/ai_animation`;

const generateAnimation = async (jwtToken, userData, options) => {
  const { style, prompt, imageDimension, assetName, seconds } = options;
  const result = await Requests.postRequest(
    baseUrlGenerate,
    jwtToken,
    {
      style,
      prompt,
      imageDimension,
      assetName,
      seconds: String(seconds),
    },
    userData
  );

  if (!result.ok) {
    await result.json();
    throw new Error("Unable to generate animation");
  }

  return result.json();
};

const STATUS_RETRY_ATTEMPTS = 2;
const STATUS_RETRY_DELAY_MS = 1500;

const checkStatus = async (aiAnimationGuid, jwtToken, userData) => {
  let lastStatus;
  let lastStatusText;
  let lastErrorText;

  for (let attempt = 0; attempt <= STATUS_RETRY_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, STATUS_RETRY_DELAY_MS));
    }
    const result = await Requests.getRequest(
      `${baseUrlStatus}${aiAnimationGuid}`,
      jwtToken,
      userData
    );
    if (result.ok) {
      return result.json();
    }
    lastStatus = result.status;
    lastStatusText = result.statusText;
    lastErrorText = await result.text().catch(() => "No error body");
    if (lastStatus < 500 || attempt >= STATUS_RETRY_ATTEMPTS) {
      throw new Error(
        `Unable to check animation status for ID '${aiAnimationGuid}': ${lastStatus} ${lastStatusText} - ${lastErrorText}`
      );
    }
  }
  throw new Error(
    `Unable to check animation status for ID '${aiAnimationGuid}': ${lastStatus} ${lastStatusText} - ${lastErrorText}`
  );
};

const fetchAnimation = async (videoId, jwtToken, userData) => {
  const result = await Requests.getRequest(
    `${baseUrlFetch}${videoId}`,
    jwtToken,
    userData
  );
  if (!result.ok) {
    throw new Error("Unable to fetch animation");
  }
  return result.json();
};

const viewPreview = async (videoId, jwtToken, userData) => {
  const result = await Requests.getRequest(
    `${baseUrlView}${videoId}`,
    jwtToken,
    userData
  );
  if (!result.ok) {
    const errorText = await result.text();
    throw new Error(
      `Unable to load animation preview: ${result.status} ${errorText}`
    );
  }
  return result.json();
};

const streamVideo = async (videoId, jwtToken, userData) => {
  const result = await Requests.getRequest(
    `${baseUrlStream}${videoId}`,
    jwtToken,
    userData
  );
  if (!result.ok) {
    throw new Error("Unable to stream animation video");
  }
  return result;
};

const downloadVideo = async (videoId, jwtToken, userData) => {
  const result = await Requests.getRequest(
    `${baseUrlDownload}${videoId}`,
    jwtToken,
    userData
  );
  if (!result.ok) {
    throw new Error("Unable to download animation video");
  }
  return result;
};

const getAllAnimations = async (jwtToken, userData, page = 0, size = 6) => {
  try {
    const result = await Requests.getRequest(
      `${baseUrlList}?page=${page}&size=${size}`,
      jwtToken,
      userData
    );
    if (!result || !result.ok) {
      throw new Error("Unable to fetch animations");
    }
    return result.json();
  } catch (error) {
    console.error("Error fetching animations:", error);
    return [];
  }
};

const searchAnimations = async (
  jwtToken,
  userData,
  searchTerm,
  page = 0,
  size = 6
) => {
  try {
    const result = await Requests.getRequest(
      `${baseUrlList}?page=${page}&size=${size}&searchText=${encodeURIComponent(
        searchTerm
      )}`,
      jwtToken,
      userData
    );
    if (!result || !result.ok) {
      throw new Error("Unable to search animations");
    }
    return result.json();
  } catch (error) {
    console.error("Error searching animations:", error);
    return [];
  }
};

const getTotalPages = async (jwtToken, userData, pageSize = 6) => {
  try {
    const result = await Requests.getRequest(
      `${baseUrlList}?page=0&size=1`,
      jwtToken,
      userData
    );
    if (!result || !result.ok) {
      throw new Error("Unable to fetch total pages");
    }
    const data = await result.json();
    return Math.ceil(data.totalElements / pageSize);
  } catch (error) {
    console.error("Error fetching total pages:", error);
    return 0;
  }
};

const getSearchTotalPages = async (
  jwtToken,
  userData,
  searchTerm,
  pageSize = 6
) => {
  try {
    const result = await Requests.getRequest(
      `${baseUrlList}?page=0&size=1&searchText=${encodeURIComponent(
        searchTerm
      )}`,
      jwtToken,
      userData
    );
    if (!result || !result.ok) {
      throw new Error("Unable to fetch search total pages");
    }
    const data = await result.json();
    return Math.ceil(data.totalElements / pageSize);
  } catch (error) {
    console.error("Error fetching search total pages:", error);
    return 0;
  }
};

const deleteAnimation = async (animationId, userData, refreshToken) => {
  const url = `${baseUrlDelete}/${animationId}`;
  const result = await Requests.deleteRequest(url, userData, refreshToken);
  if (!result.ok) {
    const errorData = await result.json();
    throw new Error(
      `Unable to delete animation: ${errorData.message || result.statusText}`
    );
  }
  return { success: true, result };
};

const handleGenerateSubmit = async (
  event,
  generateFn,
  formData,
  setStatusData,
  setIsGenerateFormSubmitted,
  setErrorMessage
) => {
  event.preventDefault();
  setStatusData(null);
  setIsGenerateFormSubmitted(true);

  try {
    const generatedAnimation = await generateFn(formData);

    if (generatedAnimation.status === "FAIL") {
      setErrorMessage(
        `Animation generation failed: ${generatedAnimation.error}`
      );
      setIsGenerateFormSubmitted(false);
      return;
    }

    setStatusData(generatedAnimation);
  } catch (error) {
    setErrorMessage(
      "An error occurred while generating the animation. Please try again."
    );
  } finally {
    setIsGenerateFormSubmitted(false);
  }
};

const handleDownload = (videoId, jwtToken, userData, filename) =>
  downloadAnimationToDisk(videoId, jwtToken, userData, filename);

const handleCopyPrompt = (selectedAnimation) =>
  copyAnimationPromptToClipboard(selectedAnimation);

const AnimationService = {
  generateAnimation,
  checkStatus,
  fetchAnimation,
  viewPreview,
  streamVideo,
  downloadVideo,
  getAllAnimations,
  searchAnimations,
  getTotalPages,
  getSearchTotalPages,
  deleteAnimation,
  handleGenerateSubmit,
  handleDownload,
  handleCopyPrompt,
};

export default AnimationService;
